import * as couponService from '@cart-services/core/couponService'
import { useFpCampaignCouponService } from '@cart-services/hooks/useFpCampaignCouponService'
import { type MergedCouponCriterionWithCoupon, type SelectedCouponGiveawayProductMap } from '@cart-services/types'
import { type CartFormValues } from '@cart-services/zod-schema/cartFormSchema'
import { DialogSelectGiveaway, DIALOG_SELECT_GIVEAWAY } from '@cart-ui/GiveawaySelect/DialogSelectGiveaway'
import { useDialog } from '@dni-hooks/useDialog'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { Fade } from '@pets-ui/Fade/Fade'
import { HStack } from '@pets-ui/HStack/HStack'
import { type PaperProps } from '@pets-ui/Paper/Paper'
import { Paper } from '@pets-ui/Paper/Paper'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useContextCampaignCart } from '../CartProvider/CampaignCartProvider'
import { GiveawaySelectController } from './GiveawaySelectController'

interface DialogSelectGiveawayExtraProps {
  couponCriterionId?: number
}

interface GiveawayListProps {
  appliedGiveaways: MergedCouponCriterionWithCoupon[] | undefined
  onOpenDialog: (criterionId: number) => void
}

const GiveawayList = ({ appliedGiveaways, onOpenDialog }: GiveawayListProps) => {
  const { formState: { errors } } = useFormContext<CartFormValues>()
  return appliedGiveaways?.map((giveaway, index) => {
    const { coupon, ...criterion } = giveaway
    return (
      <VStack key={criterion.id} gap={1}>
        <GiveawaySelectController
          index={index}
          coupon={coupon}
          criterion={criterion}
          onOpenDialog={onOpenDialog}
        />
        {errors?.giveaways?.[index]?.message && <Text color={colors.red600}>{errors.giveaways[index]?.message}</Text>}
      </VStack>
    )
  })
}

export const CardGiveaways = (props: PaperProps) => {
  const { campaignId, countryCode, campaignCode } = useContextCampaignCart()
  const {
    setDefaultSelectedGiveaways,
    appliedGiveaways,
    updateSelectedGiveaways,
    selectedCouponGiveawayMap,
  } = useFpCampaignCouponService(campaignId, countryCode, campaignCode)
  const { clearErrors } = useFormContext()
  const [selectingCriterionId, setSelectingCriterionId] = useState<number>()
  const { open, closeDialog, openDialog } = useDialog<DialogSelectGiveawayExtraProps>(DIALOG_SELECT_GIVEAWAY)

  useEffect(() => {
    /**
     * 驗證購物車資料時先不更新贈品
     * 否則重新取完商品資料後就會先觸發更新了所選的贈品，也就不會觸發庫存驗證的錯誤提示
     */
    // if (isCartInfoValidating) return
    setDefaultSelectedGiveaways()
  }, [setDefaultSelectedGiveaways])

  const handleOpenDialog = useCallbackEvent((criterionId: number) => {
    setSelectingCriterionId(criterionId)
    openDialog({ couponCriterionId: criterionId })
  })

  const currentGiveaway = useMemo(() => {
    return appliedGiveaways?.find(giveaway => giveaway.id === selectingCriterionId)
  }, [appliedGiveaways, selectingCriterionId])

  const currentGiveawayIndex = useMemo(() => {
    return appliedGiveaways?.findIndex(giveaway => giveaway.id === selectingCriterionId) ?? -1
  }, [appliedGiveaways, selectingCriterionId])

  const currentGiveawayProps = useMemo(() => {
    if (!currentGiveaway) return null
    const { coupon, ...criterion } = currentGiveaway
    return couponService.getGiveawaySelectProps(coupon, criterion, selectedCouponGiveawayMap)
  }, [currentGiveaway, selectedCouponGiveawayMap])

  const handleSubmit = useCallbackEvent((selectedCouponGiveawayProductMap: SelectedCouponGiveawayProductMap) => {
    if (!currentGiveawayProps || currentGiveawayIndex === -1) return

    clearErrors(`giveaways.${currentGiveawayIndex}`)
    updateSelectedGiveaways(currentGiveawayProps.couponCriterionId, selectedCouponGiveawayProductMap)
    closeDialog()
  })

  return (
    <>
      {appliedGiveaways?.length !== 0 && (
        <Fade in={appliedGiveaways && appliedGiveaways.length > 0} duration={500}>
          <Paper borderRadius={8} p={3} elevation={0} {...props}>
            <HStack gap={2} mb={2.5} alignItems="center">
              <Text fontWeight={600}>贈品</Text>
              <Text variant="caption" color={colors.grey500}>*贈品贈完為止</Text>
            </HStack>
            <VStack gap={2.5}>
              <GiveawayList appliedGiveaways={appliedGiveaways} onOpenDialog={handleOpenDialog} />
            </VStack>
          </Paper>
        </Fade>
      )}
      {currentGiveawayProps && (
        <DialogSelectGiveaway
          key={selectingCriterionId}
          open={open}
          onClose={closeDialog}
          title={currentGiveawayProps.title}
          options={currentGiveawayProps.options}
          isRepeatable={currentGiveawayProps.isRepeatable}
          isGiveAll={currentGiveawayProps.isGiveAll}
          isStockPartiallyNotEnough={currentGiveawayProps.isStockPartiallyNotEnough}
          accumulatedGiftSelectionCount={currentGiveawayProps.accumulatedGiftSelectionCount}
          defaultGiveawayValues={currentGiveawayProps.defaultGiveawayValues}
          onSubmit={handleSubmit}
        />
      )}
    </>
  )
}
